import { Card, List, Button, Skeleton, message } from 'antd'
import {
  PlusOutlined,
  UploadOutlined,
  FileTextOutlined,
  BellOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'

interface QuickAction {
  id: string
  title: string
  description: string
  icon: string
  path: string
  color: string
  state?: Record<string, unknown> | null
}

interface QuickActionsProps {
  actions: QuickAction[]
  loading?: boolean
  /** 是否有系统设置访问权限（控制「更多」按钮） */
  canAccessSettings?: boolean
  hasPermission?: (code: string) => boolean
}

function getRequiredPermission(path: string, state?: Record<string, unknown> | null): string | null {
  if (path === '/data') return 'data_sources:list'
  if (path === '/assets') {
    if (state?.openAddModal) return 'asset_products:create'
    return 'asset_products:list'
  }
  if (path === '/analytics') return 'analytics:reports_list'
  if (path === '/settings') return 'settings:profile_get'
  return null
}

const QuickActions = ({
  actions,
  loading = false,
  canAccessSettings = true,
  hasPermission = () => true,
}: QuickActionsProps) => {
  const navigate = useNavigate()

  const allowedActions = actions.filter((action) => {
    const perm = getRequiredPermission(action.path, action.state)
    if (!perm) return true
    return hasPermission(perm)
  })

  const iconMap: Record<string, React.ReactNode> = {
    plus: <PlusOutlined />,
    upload: <UploadOutlined />,
    'file-text': <FileTextOutlined />,
    bell: <BellOutlined />,
  }

  const colorMap: Record<string, string> = {
    blue: '#3b82f6',
    green: '#10b981',
    purple: '#8b5cf6',
    orange: '#f59e0b',
    red: '#ef4444',
  }

  const handleActionClick = (action: QuickAction) => {
    const perm = getRequiredPermission(action.path, action.state)
    if (perm && !hasPermission(perm)) {
      message.warning('暂无该操作权限')
      return
    }
    navigate(action.path, { state: action.state ?? undefined })
  }

  if (loading) {
    return (
      <Card title="快速操作">
        <Skeleton active paragraph={{ rows: 4 }} />
      </Card>
    )
  }

  return (
    <Card
      title="快速操作"
      extra={
        canAccessSettings ? (
          <Button type="link" size="small" onClick={() => navigate('/settings')}>
            更多
          </Button>
        ) : null
      }
    >
      <List
        dataSource={allowedActions}
        renderItem={(action) => (
          <List.Item
            className="cursor-pointer hover:bg-gray-50 rounded-lg p-2 transition-colors"
            onClick={() => handleActionClick(action)}
          >
            <List.Item.Meta
              avatar={
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${colorMap[action.color]}15` }}
                >
                  <div style={{ color: colorMap[action.color], fontSize: '18px' }}>
                    {iconMap[action.icon]}
                  </div>
                </div>
              }
              title={
                <div className="font-medium text-gray-800">{action.title}</div>
              }
              description={
                <div className="text-gray-500 text-sm">{action.description}</div>
              }
            />
            <ArrowRightOutlined className="text-gray-400" />
          </List.Item>
        )}
      />
    </Card>
  )
}

export default QuickActions